/**
 * AI CONTEXT: service - SMS Consent Management Service
 * Journal: J-20250903-01
 * 
 * Purpose:
 * - Handle SMS consent updates during registration and checkout
 * - Integrate with Lambda functions for consent management
 * - Provide utility functions for SMS-related operations
 * - Support compliance requirements and user preferences
 * 
 * Architecture decisions:
 * - Use authorizedFetch for authenticated API calls
 * - Support both registration and checkout flows
 * - Handle phone number formatting and validation
 * - Provide clear error handling and user feedback
 * 
 * Future considerations:
 * - Add SMS preference management in user profile
 * - Support bulk SMS operations for admins
 * - Add delivery tracking and analytics
 */

import { authorizedFetch } from './apiClient';

/**
 * Format an admin bulk SMS message with compliance text and optional media link.
 * Notes:
 * - If the admin is sending **SMS-only**, we can include a URL in the SMS body (link-style "picture message").
 * - If the admin is sending **MMS**, the backend should deliver the media via Pinpoint v2 SendMediaMessage,
 *   and we should NOT append the media URL to the SMS body (to avoid bloating/extra segments).
 */
export const formatAdminBulkMessage = ({ message, mediaUrl }) => {
  const base = (message || '').trim();
  const complianceFooter = " Msg&data rates may apply. Reply STOP to unsubscribe.";
  const prefix = "Tolbert Tactical: ";

  const parts = [];
  if (base) parts.push(base);
  if (mediaUrl) parts.push(mediaUrl);

  return `${prefix}${parts.join('\n')}${complianceFooter}`;
};

/**
 * Update SMS consent for a user
 * @param {Object} params - Consent parameters
 * @param {string} params.phoneE164 - Phone number in E.164 format
 * @param {boolean} params.optIn - Whether user is opting in
 * @param {string} params.source - Source of consent (register, checkout, profile, admin)
 * @returns {Promise<Object>} API response
 */
export const updateSmsConsent = async ({ phoneE164, optIn, source }) => {
  try {
    const response = await authorizedFetch('/user/sms/consent', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        phoneE164,
        optIn,
        source
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to update SMS consent');
    }

    return await response.json();
  } catch (error) {
    console.error('SMS consent update failed:', error);
    throw error;
  }
};

/**
 * Format phone number to E.164 format
 * @param {string} phone - Raw phone number input
 * @returns {string|null} Formatted E.164 phone number or null if invalid
 */
export const formatPhoneToE164 = (phone) => {
  if (!phone) return null;
  
  // Remove all non-digit characters
  const digits = phone.replace(/\D/g, '');
  
  // Handle US numbers
  if (digits.length === 10) {
    return `+1${digits}`;
  } else if (digits.length === 11 && digits.startsWith('1')) {
    return `+${digits}`;
  } else if (digits.length > 11) {
    // International number, assume it's already in E.164
    return `+${digits}`;
  }
  
  return null;
};

/**
 * Validate phone number format
 * @param {string} phone - Phone number to validate
 * @returns {boolean} Whether phone number is valid
 */
export const validatePhoneNumber = (phone) => {
  if (!phone) return false;
  
  const e164 = formatPhoneToE164(phone);
  return e164 !== null;
};

/**
 * Send test SMS message (admin/internal use)
 * @param {Object} params - Test SMS parameters
 * @param {string} params.to - Recipient phone number in E.164 format
 * @param {string} params.message - Message content
 * @returns {Promise<Object>} API response
 */
export const sendTestSms = async ({ to, message }) => {
  try {
    const response = await authorizedFetch('/notify/sms/sns', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        to,
        message
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to send test SMS');
    }

    return await response.json();
  } catch (error) {
    console.error('Test SMS send failed:', error);
    throw error;
  }
};

/**
 * Get SMS consent status for current user
 * @returns {Promise<Object|null>} User's SMS consent status or null if not found
 */
export const getSmsConsentStatus = async () => {
  // This would typically come from the user's profile or Cognito attributes
  // For now, we'll return null and let the calling component handle it
  return null;
};

/**
 * Check if user can receive SMS messages
 * @param {Object} user - User object with SMS consent attributes
 * @returns {boolean} Whether user can receive SMS messages
 */
export const canReceiveSms = (user) => {
  if (!user) return false;
  
  // Check Cognito custom attributes - user object has phone directly, not in attributes
  const smsOptIn = user.attributes?.['custom:sms_opt_in'];
  const phoneNumber = user.phone || user.attributes?.phone_number;
  
  return smsOptIn === 'true' && !!phoneNumber;
};

/**
 * Get user's phone number in E.164 format
 * @param {Object} user - User object with phone number attribute
 * @returns {string|null} Formatted phone number or null if not available
 */
export const getUserPhoneE164 = (user) => {
  if (!user) return null;
  
  // Check both user.phone (from auth service) and user.attributes.phone_number (direct Cognito)
  const phoneNumber = user.phone || user.attributes?.phone_number;
  if (!phoneNumber) return null;
  
  return formatPhoneToE164(phoneNumber);
};

/**
 * Handle SMS consent during registration
 * @param {Object} params - Registration parameters
 * @param {string} params.phone - Raw phone number input
 * @param {boolean} params.smsConsent - Whether user consented to SMS
 * @returns {Promise<Object>} Consent update result
 */
export const handleRegistrationSmsConsent = async ({ phone, smsConsent }) => {
  if (!smsConsent) {
    return { success: true, message: 'SMS consent not requested' };
  }
  
  const phoneE164 = formatPhoneToE164(phone);
  if (!phoneE164) {
    throw new Error('Invalid phone number format');
  }
  
  return await updateSmsConsent({
    phoneE164,
    optIn: true,
    source: 'register'
  });
};

/**
 * Handle SMS consent during checkout
 * @param {Object} params - Checkout parameters
 * @param {string} params.phone - Raw phone number input
 * @param {boolean} params.smsConsent - Whether user consented to SMS
 * @returns {Promise<Object>} Consent update result
 */
export const handleCheckoutSmsConsent = async ({ phone, smsConsent }) => {
  if (!smsConsent) {
    return { success: true, message: 'SMS consent not requested' };
  }
  
  const phoneE164 = formatPhoneToE164(phone);
  if (!phoneE164) {
    throw new Error('Invalid phone number format');
  }
  
  return await updateSmsConsent({
    phoneE164,
    optIn: true,
    source: 'checkout'
  });
};

/**
 * Opt out of SMS messages
 * @param {string} phone - Phone number to opt out
 * @returns {Promise<Object>} Opt-out result
 */
export const optOutOfSms = async (phone) => {
  const phoneE164 = formatPhoneToE164(phone);
  if (!phoneE164) {
    throw new Error('Invalid phone number format');
  }
  
  return await updateSmsConsent({
    phoneE164,
    optIn: false,
    source: 'profile'
  });
};

/**
 * Get SMS compliance text for UI display
 * @returns {Object} Compliance text and links
 */
export const getSmsComplianceText = () => ({
  consentText: "I consent to receive SMS messages from Tolbert Tactical, including order confirmations, shipping updates, product alerts, cart reminders, and giveaway notifications. Msg & data rates may apply. Reply STOP to unsubscribe. Up to 4 msgs/month.",
  disclaimer: "Msg & data rates may apply. Reply STOP to unsubscribe.",
  frequency: "Up to 4 msgs/month",
  privacyLink: "/legal#privacy",
  termsLink: "/legal#terms",
  notRequired: "Consent is not required as a condition of purchase."
});

/**
 * Send bulk SMS messages to multiple users (admin only)
 * @param {Object} params - Bulk SMS parameters
 * @param {Array} params.recipients - Array of user objects with phone numbers
 * @param {string} params.message - Message content
 * @param {string} params.source - Source of the message (admin, giveaway, etc.)
 * @returns {Promise<Object>} API response with delivery status
 */
export const sendBulkSms = async ({ recipients, message, source = 'admin', mediaUrl, mediaLinkUrl, mediaRef, sendAsMms }) => {
  try {
    const data = await authorizedFetch('/admin/sms/bulk', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        recipients,
        message,
        source,
        mediaUrl,
        mediaLinkUrl,
        mediaRef,
        // When true, backend should attempt true MMS (SendMediaMessage) and NOT fall back to SMS link.
        sendAsMms: !!sendAsMms
      })
    });

    // authorizedFetch already handles JSON parsing and error responses
    return data;
  } catch (error) {
    console.error('Bulk SMS send failed:', error);
    throw error;
  }
};

/**
 * Get users with SMS opt-in status for admin bulk messaging
 * @param {Array} users - Array of user objects
 * @returns {Array} Users who have opted in to SMS
 */
export const getSmsOptedInUsers = (users) => {
  return users.filter(user => {
    // Check if user has phone number and SMS opt-in
    const hasPhone = (user.phone && user.phone.trim() !== '') || 
                     (user.attributes?.phone_number && user.attributes.phone_number.trim() !== '');
    const hasOptIn = user.attributes?.['custom:sms_opt_in'] === 'true' || 
                     user.smsOptIn === true;
    return hasPhone && hasOptIn;
  });
};

/**
 * Format message with compliance text
 * @param {string} message - Base message content
 * @param {string} source - Source of the message (admin, giveaway, etc.)
 * @returns {string} Message with compliance footer
 */
export const formatComplianceMessage = (message, source = 'admin') => {
  const complianceFooter = " Msg&data rates may apply. Reply STOP to unsubscribe.";
  
  // Add "Tolbert Tactical:" prefix for admin messages
  if (source === 'admin') {
    return `Tolbert Tactical: ${message}${complianceFooter}`;
  }
  
  return message + complianceFooter;
};

/**
 * Check if SMS features are enabled
 * @returns {boolean} Whether SMS features are available
 */
export const isSmsEnabled = () => {
  // This could check environment variables or feature flags
  // For now, assume SMS is enabled if we're in production
  return process.env.NODE_ENV === 'production' || process.env.REACT_APP_SMS_ENABLED === 'true';
};
